import React, { useState, useCallback } from 'react';
import type { FieldSetFormData, FieldSetFormLabels, FieldSetFormProps } from '../../shared/types/fieldSetForm';

export type { FieldSetFormData, FieldSetFormLabels, FieldSetFormProps };

export function FieldSetForm({
  initialData,
  onSubmit,
  onCancel,
  errors = {},
  labels: l,
  mode,
  isSubmitting = false,
}: FieldSetFormProps) {
  const [formData, setFormData] = useState<FieldSetFormData>(initialData);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, name: e.target.value }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  }, [formData, onSubmit]);

  return (
    <form onSubmit={handleSubmit}>
      {/* Error messages */}
      {Object.keys(errors).length > 0 && (
        <div className="alert alert-danger mb-3">
          <ul className="mb-0">
            {Object.entries(errors).flatMap(([field, messages]) =>
              messages.map((msg, i) => (
                <li key={`${field}-${i}`}>{msg}</li>
              ))
            )}
          </ul>
        </div>
      )}

      {/* Name input */}
      <div className="form-group row my-3">
        <div className="col-12">
          <div className="form-floating">
            <input
              type="text"
              className="form-control"
              id="field-set-name"
              value={formData.name}
              onChange={handleNameChange}
              required
              autoFocus={mode === 'new'}
              placeholder={l.name}
            />
            <label htmlFor="field-set-name" className="form-label">{l.name}</label>
          </div>
        </div>
      </div>

      {/* Form Buttons */}
      <div className="form-group d-flex justify-content-end buttons-wrapper">
        <button
          type="button"
          className="btn btn-link me-3"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {l.cancel}
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
          data-testid="save-button"
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {l.save}
            </>
          ) : (
            l.save
          )}
        </button>
      </div>
    </form>
  );
}

export default FieldSetForm;
